import React, { useState } from 'react';
import { Header } from './Header';
import { Footer } from './Footer';

const SpiritualToolbox = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleBuildYourOwn = () => {
    setShowPopup(true);
  };

  const handleYesClick = () => {
    setShowPopup(false);
    setShowEmailForm(true);
  };

  const handleNoClick = () => {
    setShowPopup(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/spiritual-toolbox-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim()
        })
      });

      if (response.ok) {
        setSubmitted(true);
        setEmail('');
        setName('');
      } else {
        const data = await response.json();
        setError(data.detail || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeEmailForm = () => {
    setShowEmailForm(false);
    setSubmitted(false);
    setError('');
    setEmail('');
    setName('');
  };

  return (
    <div>
      {/* Add Header with Navigation */}
      <Header />
      
      <section id="spiritual-toolbox" className="py-20 bg-gradient-to-br from-amber-50 to-orange-100" style={{ marginTop: '80px' }}>
      <div className="container mx-auto px-6">
        
        {/* Hero Image */}
        <div className="text-center mb-12">
          <img 
            src="/spiritual-toolbox-hero.png" 
            alt="Do You Have a Spiritual Toolbox?" 
            className="mx-auto max-w-full h-auto rounded-lg shadow-lg"
            style={{ maxHeight: '500px' }}
          />
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-amber-900 mb-8">
            Do You Have a Spiritual Toolbox?
          </h1>
          <p className="text-xl text-amber-800 leading-relaxed mb-12">
            Just as a craftsman needs the right tools to build something beautiful and lasting, 
            we need spiritual tools to build a strong, resilient faith life. Your spiritual toolbox 
            is a collection of practices, scriptures, and resources that keep you anchored in God 
            when life gets heavy.
          </p>
        </div>

        {/* Explanation Image */}
        <div className="text-center mb-12">
          <img 
            src="/spiritual-toolbox-explanation.png" 
            alt="What is a Spiritual Toolbox?" 
            className="mx-auto max-w-full h-auto rounded-lg shadow-lg"
            style={{ maxHeight: '800px' }}
          />
        </div>

        {/* Build Your Own Button */}
        <div className="text-center mb-16">
          <button 
            onClick={handleBuildYourOwn}
            className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-4 px-8 rounded-lg text-xl transition-colors duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
          >
            Build Your Own Spiritual Toolbox
          </button>
        </div>

        {/* Scripture Section */}
        <div className="max-w-3xl mx-auto bg-white bg-opacity-80 rounded-lg p-8 shadow-lg">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold text-amber-900 mb-4">Why You Need a Spiritual Toolbox</h3>
            <blockquote className="text-lg italic text-amber-800 border-l-4 border-amber-600 pl-6">
              "But his delight is in the law of the Lord, and on his law he meditates day and night. 
              He is like a tree planted by streams of water that yields its fruit in its season, 
              and its leaf does not wither. In all that he does, he prospers."
              <footer className="text-amber-700 mt-2 not-italic">- Psalm 1:2-3</footer>
            </blockquote>
          </div>
          <p className="text-amber-800 text-center leading-relaxed">
            Every believer needs tools to help them grow strong roots in their faith. 
            When we consistently use spiritual practices—prayer, scripture, worship, community—
            we become like trees planted by streams of water, flourishing regardless of the seasons of life.
          </p>
        </div>
      </div>

      {/* Yes/No Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
              Ready to Build Your Spiritual Toolbox?
            </h3>
            <p className="text-amber-800 mb-8 text-center">
              Would you like to receive our comprehensive Spiritual Toolbox worksheet 
              to help you create your own personalized collection of faith tools?
            </p>
            <div className="flex space-x-4">
              <button 
                onClick={handleYesClick}
                className="flex-1 bg-amber-700 hover:bg-amber-800 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Yes, Send It!
              </button>
              <button 
                onClick={handleNoClick}
                className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-6 rounded-lg transition-colors duration-300"
              >
                Not Right Now
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Email Form Popup */}
      {showEmailForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
            {!submitted ? (
              <>
                <h3 className="text-2xl font-bold text-amber-900 mb-6 text-center">
                  Get Your Spiritual Toolbox Worksheet
                </h3>
                <p className="text-amber-800 mb-6 text-center">
                  Enter your information below and we'll send you the complete worksheet 
                  to help you build your personalized spiritual toolbox.
                </p>
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label className="block text-amber-900 font-semibold mb-2">Name (Optional)</label>
                    <input 
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-amber-900 font-semibold mb-2">Email Address *</label>
                    <input 
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-600"
                      placeholder="your.email@example.com"
                    />
                  </div>
                  {error && (
                    <div className="text-red-600 text-center font-semibold">
                      {error}
                    </div>
                  )}
                  <div className="flex space-x-4 pt-4">
                    <button 
                      type="submit"
                      disabled={isSubmitting}
                      className="flex-1 bg-amber-700 hover:bg-amber-800 disabled:bg-gray-400 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300"
                    >
                      {isSubmitting ? 'Sending...' : 'Send My Toolbox'}
                    </button>
                    <button 
                      type="button"
                      onClick={closeEmailForm}
                      className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-3 px-6 rounded-lg transition-colors duration-300"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="text-6xl text-green-500 mb-4">✓</div>
                  <h3 className="text-2xl font-bold text-amber-900 mb-4">
                    Toolbox Sent Successfully!
                  </h3>
                  <p className="text-amber-800 mb-6">
                    Check your email for your Spiritual Toolbox worksheet. 
                    We're excited to see you grow in your faith journey!
                  </p>
                  <button 
                    onClick={closeEmailForm}
                    className="bg-amber-700 hover:bg-amber-800 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-300"
                  >
                    Close
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      </section>
      
      {/* Add Footer for consistency */}
      <Footer />
    </div>
  );
};

export default SpiritualToolbox;